import { MouseEvent, PointerEvent as ReactPointerEvent, useCallback, useRef } from 'react'

const LONG_PRESS_DURATION_MS = 500
const MOVE_CANCEL_THRESHOLD_PX = 10

const isInsideModal = (target: EventTarget | null): boolean => (
    target instanceof Element && !! target.closest('.paco-modal')
)

interface UseLongPressResult {
    onPointerDown: (event: ReactPointerEvent) => void
    onPointerMove: (event: ReactPointerEvent) => void
    onPointerUp: () => void
    onPointerLeave: () => void
    onContextMenu: (event: MouseEvent) => void
    onClickCapture: (event: MouseEvent) => void
}

type LongPressEnabled = boolean | ((target: EventTarget | null) => boolean)

const resolveEnabled = (enabled: LongPressEnabled, target: EventTarget | null): boolean => (
    typeof enabled === 'function' ? enabled(target) : enabled
)

export const useLongPress = (
    onLongPress: (target: EventTarget | null) => void,
    enabled: LongPressEnabled = true,
): UseLongPressResult => {
    const timeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined)
    const startPositionRef = useRef<{ x: number, y: number } | null>(null)
    const pressTargetRef = useRef<EventTarget | null>(null)
    const firedFromPointerRef = useRef(false)

    const clear = useCallback((): void => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current)
            timeoutRef.current = undefined
        }

        startPositionRef.current = null
    }, [])

    const triggerFromPointer = useCallback((): void => {
        firedFromPointerRef.current = true
        onLongPress(pressTargetRef.current)
    }, [onLongPress])

    const onPointerDown = useCallback((event: ReactPointerEvent): void => {
        if (isInsideModal(event.target) || ! resolveEnabled(enabled, event.target)) {
            return
        }

        startPositionRef.current = { x: event.clientX, y: event.clientY }
        pressTargetRef.current = event.target
        timeoutRef.current = setTimeout(triggerFromPointer, LONG_PRESS_DURATION_MS)
    }, [enabled, triggerFromPointer])

    const onPointerMove = useCallback((event: ReactPointerEvent): void => {
        const start = startPositionRef.current

        if (! start) {
            return
        }

        const distance = Math.hypot(event.clientX - start.x, event.clientY - start.y)

        if (distance > MOVE_CANCEL_THRESHOLD_PX) {
            clear()
        }
    }, [clear])

    const onContextMenu = useCallback((event: MouseEvent): void => {
        if (isInsideModal(event.target) || ! resolveEnabled(enabled, event.target)) {
            return
        }

        event.preventDefault()
        onLongPress(event.target)
    }, [enabled, onLongPress])

    const onClickCapture = useCallback((event: MouseEvent): void => {
        if (firedFromPointerRef.current) {
            firedFromPointerRef.current = false
            event.preventDefault()
            event.stopPropagation()
        }
    }, [])

    return {
        onPointerDown,
        onPointerMove,
        onPointerUp: clear,
        onPointerLeave: clear,
        onContextMenu,
        onClickCapture,
    }
}
